<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { Button as KButton } from '@progress/kendo-vue-buttons'
import {
  AutoComplete,
  type AutoCompleteChangeEvent,
} from '@progress/kendo-vue-dropdowns'
import { NumericTextBox as KNumericTextBox } from '@progress/kendo-vue-inputs'

import { ref, onMounted } from 'vue'
import { GetSkuListBySearchWord } from '../../api/Sku'
import { useLogger } from 'vue-logger-plugin'
import AoeSkuDetail from '../../components/Controls/AoeSkuDetail.vue'
import {
  GetBeeNumberAuthenicatedUser,
  GetBeeNumberSite,
  GetOrderTypeId,
  GetImageRootUrl,
} from '../../stores/SettingsStore'
import { SetIsLoading } from '../../services/AoeHelpers.ts'
import { handleError } from '../../services/ErrorService.ts'
import { AddItemToCart } from '../../services/CartService'
import type { IAoeCart, IAoeCartEarnedDiscountItem } from '../../types/CartTypes'
import { GetFullProductImageDictionary, GetIsOnlyAvailableForHost, GetIsOnlyAvailableForDiscount } from '../../services/SkuService.ts'
import { GetAvailableQty } from '../../services/InventoryService.ts'
import Swal from "sweetalert2"
const logger = useLogger()

const selectedSku = ref<string>('')
const orderTypeId = ref<string>('')
const imageRootURL = ref<string>('')
const isLoading = ref<boolean>(false)
const loadingMessage = ref<string>('')
const quantity = ref<number>(1)
const availableQty = ref<number>(0)
const isHostOnly = ref<boolean>(false)
const isDiscountOnlyFlag = ref<boolean>(false)

const emit = defineEmits(['update-cart'])

const props = defineProps<{
  cart: IAoeCart | null
  showheader: boolean
  poediscplanleveldiscid?: number
  skuList?: IAoeCartEarnedDiscountItem[]
  hideQuantity?: boolean | null
  twoRows?: boolean
}>()

logger.debug('AoeSingleLineEntry props:', props)

interface iViewModel {
  skuName: string
  skuCode: string
  productName: string
  productAndSku: string
  fullProductImageUrl: string
}

const viewModels = ref<iViewModel[]>([])
const productImageUrls = ref<Record<string, string>>({})
const latestSearchRequestId = ref(0)

function invalidatePendingSearches() {
  latestSearchRequestId.value += 1
}

function isLatestRequest(requestId: number, keywordAtRequestTime: string): boolean {
  console.log('isLatestRequest', requestId, keywordAtRequestTime, latestSearchRequestId.value, autoCompleteTextValue.value.trim())
  return (
    requestId === latestSearchRequestId.value &&
    keywordAtRequestTime === autoCompleteTextValue.value.trim()
  )
}

function changeEntryTextHandler(event: AutoCompleteChangeEvent) {
  autoCompleteTextValue.value = event.target.value
  logger.debug('changeEntryTextHandler event:', event)
  if (
    event.event != undefined &&
    event.event.currentTarget.className === 'k-clear-value'
  ) {
    logger.debug('clearSkuSearchTextHandler')
    invalidatePendingSearches()
    selectedSku.value = ''
    viewModels.value = []
    isLoading.value = false
    loadingMessage.value = ''
  } else {
    if (props.skuList) {
      if (event.target.value.length >= 1) {
        logger.debug('loading viewModels from props.skuList')
        viewModels.value = []
        props.skuList
          .filter(
            sku =>
              sku.skuCode
                .toLowerCase()
                .includes(event.target.value.toLowerCase()) ||
              sku.skuName
                .toLowerCase()
                .includes(event.target.value.toLowerCase()),
          )
          .forEach(sku => {
            const vm = {
              skuName: sku.skuName,
              skuCode: sku.skuCode,
              productName: sku.skuName,
              productAndSku: sku.skuName + ' - ' + sku.skuCode,
              fullProductImageUrl: productImageUrls.value[sku.productCode],
            }
            viewModels.value.push(vm)
          })
        if (viewModels.value.length === 0) {
          loadingMessage.value = t('labels.AutoCompleteNoResults')
        }
      }
    } else if (event.target.value.length >= 3) {
      const keywordAtRequestTime = event.target.value.trim()
      const requestId = ++latestSearchRequestId.value

      isLoading.value = true
      loadingMessage.value = t('labels.AutoCompleteLoading')
      GetSkuListBySearchWord(orderTypeId.value, keywordAtRequestTime, 10).then(
        response => {
          if (!isLatestRequest(requestId, keywordAtRequestTime)) {
            logger.debug('Ignoring stale autocomplete response for outdated request', requestId)
            return
          }

          logger.debug(
            'searchClickedHandler',
            orderTypeId.value,
            keywordAtRequestTime,
            response,
          )
          viewModels.value = []
          for (const locCon of response.items) {
            const eH = {
              skuName: locCon.skuName,
              skuCode: locCon.skuCode,
              productName: locCon.product.productName,
              productAndSku:
                locCon.product.productName + ' - ' + locCon.skuCode,
              fullProductImageUrl:
                imageRootURL.value +
                '/catalogcontent/' +
                locCon.product.attributes.CatalogImage,
            }
            viewModels.value.push(eH)
          }

          isLoading.value = false
          if (viewModels.value.length === 0) {
            loadingMessage.value = t('labels.AutoCompleteNoResults')
          }
        },
      )
    } else {
      invalidatePendingSearches()
      isLoading.value = false
      viewModels.value = []
      if (event.target.value.length > 0) {
        loadingMessage.value = t('labels.AutoCompleteMinCharacters')
      } else {
        loadingMessage.value = ''
      }
    }
  }
}

async function addToCartClickedHandler(event: MouseEvent) {
  logger.debug('addToCartClickedHandler', event)

  if (!selectedSku.value) {
    return;
  }

  if (isHostOnly.value && !props.cart?.isHostCart) {
    await Swal.fire({
      title: t('alerts.HostOnlyItemTitle'),
      text: t('alerts.HostOnlyItemMessage'),
      icon: 'error',
      confirmButtonText: t('labels.Okay')
    });
    return;
  }

  if (isDiscountOnlyFlag.value) {
    await Swal.fire({
      title: t('alerts.DiscountOnlyItemTitle'),
      text: t('alerts.DiscountOnlyItemMessage'),
      icon: 'error',
      confirmButtonText: t('labels.Okay')
    });
    return;
  }

  SetIsLoading(true)

  try {
    // Check product availability before adding to cart
    const availableQty = await GetAvailableQty(orderTypeId.value, selectedSku.value);

    if (availableQty < 1) {
      // Show error message for out of stock items
      await Swal.fire({
        title: t('partyOrderEntry.OutOfStock'),
        text: t('partyOrderEntry.ItemNotAvailable'),
        icon: 'error',
        confirmButtonText: t('labels.Okay')
      });
      SetIsLoading(false);
      return;
    }

    const beeNumberPayee = await GetBeeNumberSite()
    const beeNumberCustomer = await GetBeeNumberAuthenicatedUser()

    if (props.cart != null) {
      AddItemToCart(
        props.cart.aoeCartIdToken,
        beeNumberPayee,
        beeNumberCustomer,
        orderTypeId.value,
        selectedSku.value,
        quantity.value || 1,
        props.poediscplanleveldiscid,
      )
        .then(() => {
          logger.debug('AddItemToCart success')
          autoCompleteTextValue.value = ''
          selectedSku.value = ''
          viewModels.value = []
          quantity.value = 1
        })
        .catch(async error => {
          await handleError(error, t)
        })
        .finally(() => {
          SetIsLoading(false)
          emit('update-cart', event)
        })
    }
  } catch (error) {
    await handleError(error, t)
    SetIsLoading(false)
  }
}

const autoCompleteTextValue = ref<string>('')

async function selectedSkuHandler(event: iViewModel) {
  logger.debug('selectedSkuHandler', event)
  selectedSku.value = event.skuCode

  // Check inventory, host-only and discount-only flags when SKU is selected
  await Promise.all([
    checkInventory(event.skuCode),
    checkHostOnly(event.skuCode),
    checkDiscountOnly(event.skuCode),
  ])
}

async function checkInventory(skuCode: string) {
  if (!skuCode) {
    availableQty.value = 0
    return
  }

  try {
    const qty = await GetAvailableQty(orderTypeId.value, skuCode)
    availableQty.value = qty
    logger.debug('Available quantity for', skuCode, ':', qty)
  } catch (error) {
    logger.error('Error checking inventory:', error)
    availableQty.value = 0
  }
}

async function checkHostOnly(skuCode: string) {
  if (!skuCode) {
    isHostOnly.value = false
    return
  }

  try {
    isHostOnly.value = await GetIsOnlyAvailableForHost(orderTypeId.value, skuCode)
    logger.debug('onlyAvailableForHost for', skuCode, ':', isHostOnly.value)
  } catch (error) {
    logger.error('Error checking host-only flag:', error)
    isHostOnly.value = false
  }
}

async function checkDiscountOnly(skuCode: string) {
  if (!skuCode) {
    isDiscountOnlyFlag.value = false
    return
  }

  try {
    isDiscountOnlyFlag.value = await GetIsOnlyAvailableForDiscount(orderTypeId.value, skuCode)
    logger.debug('onlyAvailableForDiscount for', skuCode, ':', isDiscountOnlyFlag.value)
  } catch (error) {
    logger.error('Error checking discount-only flag:', error)
    isDiscountOnlyFlag.value = false
  }
}

function isOutOfStock(): boolean {
  return selectedSku.value !== '' && availableQty.value < 1
}

function isHostOnlyAndNotHostCart(): boolean {
  return selectedSku.value !== '' && isHostOnly.value && !props.cart?.isHostCart
}

function isDiscountOnly(): boolean {
  return selectedSku.value !== '' && isDiscountOnlyFlag.value
}

function handleKeyDown(event: KeyboardEvent) {
  logger.debug('handleKeyDown', event)
  if (event.key === 'Enter' && viewModels.value.length > 0) {
    // Find the matching item based on the current text value
    const matchingItem = viewModels.value.find(item =>
      item.productAndSku.toLowerCase() === autoCompleteTextValue.value.toLowerCase()
    )

    if (matchingItem) {
      logger.debug('Enter key pressed - setting selectedSku to:', matchingItem.skuCode)
      selectedSku.value = matchingItem.skuCode
      checkInventory(matchingItem.skuCode)
    }
  }
}

onMounted(async () => {
  logger.debug('AoeSingleLineEntry onMounted', props.cart)
  if (props.cart?.orderTypeId != null) {
    orderTypeId.value = props.cart.orderTypeId
  } else {
    orderTypeId.value = await GetOrderTypeId()
  }
  logger.debug('orderTypeId:', orderTypeId.value)
  imageRootURL.value = await GetImageRootUrl()
  if (props.skuList) {
    productImageUrls.value = await GetFullProductImageDictionary(
      props.skuList.map(sku => sku.productCode),
    )
    logger.debug('productImageUrls:', productImageUrls)
  }
})
</script>
<template>
  <div class="aoe-single-line-entry">
    <div
      v-if="props.showheader"
      class="row party-order-entry-section-header-row"
    >
      <div
        class="col-12 party-order-entry-section-header-div single-line-entry-section-header"
      >
        {{ t('partyOrderEntry.ItemEntry') }}
      </div>
    </div>
    <div class="row mt-2 single-line-auto-complete-row">
      <div
        class="col-12 mb-2 single-line-auto-complete-div"
        :class="props.twoRows ? 'two-rows' : props.hideQuantity ? 'col-md-9' : 'col-md-7'"
      >
        <AutoComplete
          class="single-line-auto-complete"
          :data-items="viewModels"
          :placeholder="t('labels.SearchForProduct')"
          :text-field="'productAndSku'"
          :item-render="AoeSkuDetail"
          list-no-data-render="listNoDataRender"
          :loading="isLoading"
          @change="changeEntryTextHandler($event)"
          @selected-sku="selectedSkuHandler"
          @keydown="handleKeyDown($event)"
          :value="autoCompleteTextValue"
        >
          <template v-slot:myTemplate="{ props }">
            <custom
              :style="props.style"
              :class="props.itemClass"
              :data-item="props.dataItem"
              :focused="props.focused"
              :index="props.index"
            />
          </template>
          <template v-slot:listNoDataRender="{}">
            <div class="k-list-no-data">
              {{ loadingMessage }}
            </div>
          </template>
        </AutoComplete>
      </div>
      <div v-if="!props.hideQuantity"
        class="col-4 single-line-quantity-div"
        :class="props.twoRows ? 'two-rows' : 'col-md-2'"
      >
        <KNumericTextBox
          :value="quantity"
          @change="quantity = $event.target.value"
          :min="1"
          :max="999"
          :step="1"
          :decimals="0"
          :placeholder="t('labels.Quantity')"
          class="single-line-quantity-input"
        />
      </div>
      <div class="col-8 single-line-button-div"
        :class="props.twoRows ? '' : 'col-md-3'"
      >
        <KButton
          @click="addToCartClickedHandler($event)"
          class="baseBtn primaryBtn single-line-add-to-cart-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'"
          :disabled="isOutOfStock() || isHostOnlyAndNotHostCart() || isDiscountOnly()"
        >
          {{
            isOutOfStock() ? t('partyOrderEntry.ItemNotAvailable') :
            isHostOnlyAndNotHostCart() ? t('alerts.HostOnlyItemButton') :
            isDiscountOnly() ? t('alerts.DiscountOnlyItemButton') :
            props.poediscplanleveldiscid == null
              ? t('labels.AddToCart')
              : t('labels.Add')
          }}
        </KButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.k-list-no-data {
  @include typography-small;
  text-align: center;
  padding: $spacing-2-5;
  color: #999999;
}

.single-line-quantity-div {
  padding-left: 8px;
  padding-right: 8px;
}

.single-line-quantity-input {
  width: 100%;
}

.single-line-add-to-cart-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.single-line-auto-complete-div.two-rows {
  line-height: unset;
  align-content: unset;
}

.two-rows {
  padding-left: calc(var(--bs-gutter-x) * 0.5);
}
</style>
